import type { Price, ProductWithPrice } from 'types';

import {
  AUDIOS_IN_PLAN,
  BASE_SEATS_PER_PLAN,
  BOTS_IN_PLAN,
  IMAGES_IN_PLAN,
  SPEECHES_WORDS_IN_PLAN,
  TEAMS_SEATS_COUNT,
  WORDS_IN_PLAN,
} from '@/constants/common';

export type BillingInterval = 'month' | 'year';

export type TeamPlan = {
  product: ProductWithPrice;
  name: string;
  seats: number;
  prices: Partial<Record<BillingInterval, Price>>;
};

/** The Teams products, cheapest seat count first, each with both prices. */
export function toTeamPlans(products: ProductWithPrice[]): TeamPlan[] {
  return products
    .map((product) => {
      const name = product.name ?? '';
      const prices: TeamPlan['prices'] = {};
      for (const price of product.prices ?? []) {
        if (price.interval === 'month' || price.interval === 'year') {
          prices[price.interval] = price as Price;
        }
      }
      return {
        product,
        name,
        seats: BASE_SEATS_PER_PLAN[name] ?? TEAMS_SEATS_COUNT,
        prices,
      };
    })
    .filter((plan) => plan.prices.month || plan.prices.year)
    .sort((a, b) => a.seats - b.seats);
}

/** What one month costs, in cents (a yearly price is spread over 12). */
export const monthlyCents = (price: Price) =>
  (price.unit_amount ?? 0) / (price.interval === 'year' ? 12 : 1);

/** What one person's seat costs a month, in cents. */
export const seatCents = (plan: TeamPlan, interval: BillingInterval) => {
  const price = plan.prices[interval];
  return price ? monthlyCents(price) / plan.seats : null;
};

/** The lowest per-person monthly price across all plans and intervals. */
export function lowestSeatCents(plans: TeamPlan[]) {
  let lowest: number | null = null;
  for (const plan of plans) {
    for (const interval of ['month', 'year'] as const) {
      const cents = seatCents(plan, interval);
      if (cents !== null && (lowest === null || cents < lowest)) {
        lowest = cents;
      }
    }
  }
  return lowest;
}

/** How much yearly billing saves against twelve monthly payments, in %. */
export function yearlySavingPercent(plan: TeamPlan) {
  const monthly = plan.prices.month?.unit_amount;
  const yearly = plan.prices.year?.unit_amount;
  if (!monthly || !yearly) return 0;
  return Math.round((1 - yearly / (monthly * 12)) * 100);
}

export function formatMoney(cents: number, currency: string, locale?: string) {
  const amount = cents / 100;
  const whole = Math.abs(amount - Math.round(amount)) < 0.005;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(whole ? Math.round(amount) : amount);
}

type PlanName = keyof typeof WORDS_IN_PLAN;

/**
 * Monthly allowances of a Teams plan. Every person on the team gets their own
 * (usage is counted per member against the owner's billing period), except
 * agents, which belong to the account.
 */
export function teamAllowances(name: string) {
  const plan = name as PlanName;
  return {
    words: WORDS_IN_PLAN[plan] ?? 0,
    credits: IMAGES_IN_PLAN[plan as keyof typeof IMAGES_IN_PLAN] ?? 0,
    speech:
      SPEECHES_WORDS_IN_PLAN[plan as keyof typeof SPEECHES_WORDS_IN_PLAN] ?? 0,
    transcriptions: AUDIOS_IN_PLAN[plan as keyof typeof AUDIOS_IN_PLAN] ?? 0,
    agents: BOTS_IN_PLAN[plan as keyof typeof BOTS_IN_PLAN] ?? 0,
  };
}
